// ─────────────────────────────────────────────────────────────────────────────
// algolia-swatch.js — CB Shopify theme
//
// US-5: Swatch data lazy-load system — images, badges, colour swatches.
//   fetchSwatchData        — fetches /products/{handle}?view=swatchdata; cached per session
//   selectRepresentative   — picks the variant that drives image/price/sale state
//   buildSwatchElements    — creates the <color-swatch-variant> DOM nodes
//   attachSwatchClickHandler — one-time capture-phase swatch click handler per card
//   applySwatchData        — orchestrates the above; wrapped in try/catch
//
// History patch: InstantSearch updates the URL via pushState AFTER re-rendering
// the DOM. When swatch data is cached, applySwatchData runs from cache before
// the URL reflects the new filter. We intercept history changes and re-apply
// when the active base_colors refinement actually changes.
//
// Depends on window.CBAlgolia (algolia-shared.js must load first).
// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener('algolia.hooks.initialize', function () {
  const {
    settings,
    collectionData,
    alignSuffix,
    getActiveBaseColors,
    makeShopifyImageSrc,
    makeShopifySrcset,
    updateHoverImage,
    formatMoney,
    fromPrice,
    lowestVisibleVariant,
  } = window.CBAlgolia

  // ── Per-session cache ─────────────────────────────────────────────────────
  // Keyed by product handle. Prevents duplicate fetches when IS re-renders
  // cards on filter change (new DOM nodes, same products in cache).
  const swatchDataCache = new Map()

  function fetchSwatchData(handle) {
    if (!swatchDataCache.has(handle)) {
      swatchDataCache.set(
        handle,
        fetch(`/products/${handle}?view=swatchdata`)
          .then((r) => r.json())
          .catch((err) => {
            swatchDataCache.delete(handle)
            throw err
          })
      )
    }
    return swatchDataCache.get(handle)
  }

  // ── Helper: pick representative variant ───────────────────────────────────
  // Scopes to the collection's size-based variant group, then biases toward a
  // variant matching any active base_colors filter, then falls back to first
  // available → first in group → first available overall → first overall.
  function selectRepresentative(swatchData, variantGroupOptions) {
    const groupVariants =
      variantGroupOptions.length > 0
        ? swatchData.filter((v) => variantGroupOptions.includes(v.size))
        : swatchData
    const effectiveVariants =
      groupVariants.length > 0 ? groupVariants : swatchData

    const activeBaseColors = getActiveBaseColors()
    const baseColorMatch =
      activeBaseColors.length > 0
        ? (effectiveVariants.find(
            (v) =>
              v.available &&
              Array.isArray(v.baseColors) &&
              v.baseColors.some((bc) => activeBaseColors.includes(bc))
          ) ??
          effectiveVariants.find(
            (v) =>
              Array.isArray(v.baseColors) &&
              v.baseColors.some((bc) => activeBaseColors.includes(bc))
          ))
        : null

    const representative =
      baseColorMatch ??
      groupVariants.find((v) => v.available) ??
      groupVariants[0] ??
      swatchData.find((v) => v.available) ??
      swatchData[0]

    return { representative, effectiveVariants }
  }

  // ── Helper: build swatch DOM nodes ────────────────────────────────────────
  function buildSwatchElements(effectiveVariants, representative, handle) {
    const colorSwatchStyle = settings.colorSwatchStyle || 'color'
    const activeColorSlug = representative?.colorSlug
    const maxSwatches = settings.maxSwatchesShow || 6
    const seenColors = new Set()
    const swatchEls = []

    for (const v of effectiveVariants) {
      if (seenColors.has(v.colorSlug)) continue
      seenColors.add(v.colorSlug)

      if (swatchEls.length < maxSwatches) {
        const colorAvailable = effectiveVariants.some(
          (gv) => gv.colorSlug === v.colorSlug && gv.available
        )
        const isActive = v.colorSlug === activeColorSlug

        const el = document.createElement('color-swatch-variant')
        el.className = [
          'product--color-swatch',
          isActive && 'checked-color',
          !colorAvailable && 'sold-out',
          colorSwatchStyle === 'image' && 'product--color-swatch-image',
        ]
          .filter(Boolean)
          .join(' ')
        el.setAttribute('data-product-handle', handle)
        el.setAttribute('data-variant-id', String(v.variantId))
        el.setAttribute('data-value', v.color)
        el.setAttribute('data-color', v.colorSlug)
        el.setAttribute('data-image-url', v.imageUrl || '')
        el.setAttribute('data-hover-image-url', v.hoverImageUrl || '')
        el.setAttribute('title', v.color)
        // CSS custom properties are per-element dynamic values — there is no
        // class-based alternative for per-swatch colours. Intentional exception
        // to the "no inline styles" rule.
        el.style.cssText =
          colorSwatchStyle === 'image' && v.imageUrl
            ? `--swatch-background-image: url(${v.imageUrl}); --swatch-background-color: ${v.colorSlug};`
            : `--swatch-background-color: ${v.colorSlug};`
        el.innerHTML = `<span class="variant--swatch-custom variant--swatch-color"><span class="swatch--variant-tooltip">${v.color}</span></span>`
        swatchEls.push(el)
      }
    }

    const overflow = seenColors.size - maxSwatches
    if (overflow > 0) {
      const link = document.createElement('a')
      link.className = 'rest__of--color-variants'
      link.href = `/products/${handle}`
      link.textContent = `+${overflow}`
      swatchEls.push(link)
    }

    return { swatchEls, colorSwatchStyle }
  }

  // ── Helper: one-time swatch click listener per card ───────────────────────
  // Uses capture phase so it fires before the native color-swatch-variant
  // bubbling listener, letting us use data-attribute selectors for all Algolia
  // card targets.
  // Extracted from applySwatchData because it is one-time lifecycle setup,
  // not a data-apply operation — runs only on the first call per card.
  function attachSwatchClickHandler(cardEl) {
    if (cardEl.hasAttribute('data-swatch-click-init')) return
    cardEl.setAttribute('data-swatch-click-init', '')
    cardEl.addEventListener(
      'click',
      (e) => {
        const swatchEl = e.target.closest('color-swatch-variant')
        if (!swatchEl) return
        e.stopImmediatePropagation()

        const handle = swatchEl.dataset.productHandle
        const variantId = swatchEl.dataset.variantId

        // Toggle active class — scoped to this card's swatch zone only
        const swatchWrapper = cardEl.querySelector('[data-swatch-zone]')
        if (swatchWrapper) {
          swatchWrapper
            .querySelectorAll('color-swatch-variant')
            .forEach((sw) => sw.classList.remove('checked-color'))
        }
        swatchEl.classList.add('checked-color')

        // Update primary image from the URL stored on the swatch element at load time
        const imageUrl = swatchEl.dataset.imageUrl
        if (imageUrl) {
          const destImg = cardEl.querySelector('[data-algolia-primary-img]')
          if (destImg) {
            destImg.src = makeShopifyImageSrc(imageUrl, 533)
            destImg.srcset = makeShopifySrcset(imageUrl)
          }
        }

        updateHoverImage(cardEl, swatchEl.dataset.hoverImageUrl || '')

        // Update all card links and quick-add button to the selected variant URL
        const variantUrl = `/products/${handle}?variant=${variantId}`
        cardEl
          .querySelectorAll('[data-algolia-card-link]')
          .forEach((l) => l.setAttribute('href', variantUrl))
        const quickAddBtn = cardEl.querySelector('[data-product-url]')
        if (quickAddBtn) {
          quickAddBtn.setAttribute('data-product-url', variantUrl)
        }
      },
      true // capture phase — fires before color-swatch-variant's own listener
    )
  }

  // ── applySwatchData ───────────────────────────────────────────────────────
  // Applies fetched swatch data to a rendered card element.
  // try/catch ensures a malformed swatchdata response doesn't leave the card
  // at opacity:0. The finally block always adds .swatch-loaded.
  function applySwatchData(cardEl, swatchData) {
    try {
      const variantGroupOptions = collectionData.variantGroupOptions || []
      const { representative, effectiveVariants } = selectRepresentative(
        swatchData,
        variantGroupOptions
      )

      // Update primary image
      if (representative?.imageUrl) {
        const imgEl = cardEl.querySelector('[data-algolia-primary-img]')
        if (imgEl) {
          imgEl.src = makeShopifyImageSrc(representative.imageUrl, 533)
          imgEl.srcset = makeShopifySrcset(representative.imageUrl)
        }
      }

      updateHoverImage(cardEl, representative?.hoverImageUrl || '')

      // Price, sale state, and availability come from the lowest-priced visible
      // variant across the WHOLE product — not the collection's variant group —
      // so the "From" price matches Quick Add / PDP (CB-40). effectiveVariants
      // stays group-scoped for the representative image and the swatch list.
      // swatchdata already excludes hidden variants, so this also resolves
      // the "From $0.00" bug when unpriced hidden variants exist.
      const isProductAvailable = swatchData.some((v) => v.available)
      const priceVariant = lowestVisibleVariant(swatchData) || representative
      const isOnSale =
        priceVariant &&
        priceVariant.compareAtPrice > priceVariant.price &&
        priceVariant.available

      // Update badge visibility — mirrors initial class state set by the template
      const showSoldOut = settings.showSoldOutBadge && !isProductAvailable
      cardEl
        .querySelectorAll('[data-algolia-sold-out-badge]')
        .forEach((el) => el.classList.toggle('hidden', !showSoldOut))
      const showSaleBadge =
        settings.showSalesBadge && isProductAvailable && isOnSale
      cardEl
        .querySelectorAll('[data-algolia-sale-badge]')
        .forEach((el) => el.classList.toggle('hidden', !showSaleBadge))
      cardEl.querySelectorAll('[data-algolia-sale-pct]').forEach((el) => {
        const valueEl = el.querySelector('.save-percentage__value')
        if (isProductAvailable && isOnSale && priceVariant) {
          const pct = Math.round(
            ((priceVariant.compareAtPrice - priceVariant.price) /
              priceVariant.compareAtPrice) *
              100
          )
          if (valueEl) valueEl.textContent = `-${pct}%`
          el.classList.remove('hidden')
        } else {
          el.classList.add('hidden')
        }
      })

      // Update price modifier classes and toggle visible structure
      const priceEl = cardEl.querySelector('[data-algolia-price]')
      if (priceEl) {
        const needsSale = isProductAvailable && isOnSale
        priceEl.classList.toggle('price--sold-out', !isProductAvailable)
        priceEl.classList.toggle('price--on-sale', needsSale)
        // Both price__sale and price__regular are always in the DOM — toggle which is visible
        priceEl
          .querySelector('.price__sale')
          ?.classList.toggle('hidden', !needsSale)
        priceEl
          .querySelector('.price__regular')
          ?.classList.toggle('hidden', needsSale)

        // Rewrite price text using lowest visible variant data
        if (priceVariant) {
          const formattedPrice = fromPrice(priceVariant.price)
          const saleEl = priceEl.querySelector('[data-algolia-price-sale]')
          const regularEl = priceEl.querySelector(
            '[data-algolia-price-regular]'
          )
          const compareEl = priceEl.querySelector(
            '[data-algolia-price-compare]'
          )
          if (saleEl) saleEl.textContent = formattedPrice
          if (regularEl) regularEl.textContent = formattedPrice
          if (compareEl && priceVariant.compareAtPrice > priceVariant.price) {
            compareEl.textContent = formatMoney(priceVariant.compareAtPrice)
          }
        }
      }

      // Populate swatch zone — mirrors settings from product-card-color-swatch.liquid
      const swatchZone = cardEl.querySelector('[data-swatch-zone]')
      if (swatchZone) {
        swatchZone.className = `product--color-swatch-wrapper justify-content-${alignSuffix}`
        swatchZone.setAttribute(
          'data-color-swatch-style',
          settings.colorSwatchStyle || 'color'
        )

        if (!settings.colorSwatchesEnabled) {
          swatchZone.replaceChildren()
        } else {
          const handle = cardEl.dataset.algoliaHandle
          const { swatchEls, colorSwatchStyle } = buildSwatchElements(
            effectiveVariants,
            representative,
            handle
          )
          swatchZone.setAttribute('data-color-swatch-style', colorSwatchStyle)
          swatchZone.replaceChildren(...swatchEls)
        }
      }

      // One-time lifecycle setup — attach swatch click handler
      attachSwatchClickHandler(cardEl)
    } catch (err) {
      console.error('[algolia] applySwatchData error:', err, cardEl)
    } finally {
      // Always mark loaded so the image fades in rather than staying invisible
      cardEl.classList.add('swatch-loaded')
    }
  }

  // ── IntersectionObserver — lazy-fetch per card ────────────────────────────
  const swatchIntersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const card = entry.target
        swatchIntersectionObserver.unobserve(card)
        fetchSwatchData(card.dataset.algoliaHandle)
          .then((swatchData) => applySwatchData(card, swatchData))
          .catch((err) => {
            console.error('[algolia] swatchdata fetch error:', err)
            card.classList.add('swatch-loaded')
          })
      })
    },
    { rootMargin: '200px' }
  )

  // ── MutationObserver — registers new cards with IntersectionObserver ──────
  // data-swatch-init guards against double-registration on IS re-renders
  // (filter changes replace DOM nodes but same handles are served from cache).
  const swatchMutationObserver = new MutationObserver(() => {
    document
      .querySelectorAll('[data-algolia-handle]:not([data-swatch-init])')
      .forEach((card) => {
        card.setAttribute('data-swatch-init', '')
        swatchIntersectionObserver.observe(card)
      })
  })
  swatchMutationObserver.observe(document.body, {
    childList: true,
    subtree: true,
  })

  // ── Re-apply on base_colors filter change ─────────────────────────────────
  // InstantSearch updates the URL via pushState AFTER re-rendering the DOM.
  // When swatch data is cached, applySwatchData runs from cache before the URL
  // reflects the new filter, so the representative variant is computed against
  // a stale URL. Intercept history changes and re-apply when the active
  // base_colors refinement actually changes.
  let prevBaseColorsKey = getActiveBaseColors().sort().join('\0')

  function reapplySwatchDataOnFilterChange() {
    const currentKey = getActiveBaseColors().sort().join('\0')
    if (currentKey === prevBaseColorsKey) return
    prevBaseColorsKey = currentKey
    document
      .querySelectorAll('[data-algolia-handle].swatch-loaded')
      .forEach((card) => {
        const handle = card.dataset.algoliaHandle
        if (swatchDataCache.has(handle)) {
          swatchDataCache
            .get(handle)
            .then((data) => applySwatchData(card, data))
        }
      })
  }

  const _pushState = history.pushState
  const _replaceState = history.replaceState
  history.pushState = function () {
    _pushState.apply(this, arguments)
    reapplySwatchDataOnFilterChange()
  }
  history.replaceState = function () {
    _replaceState.apply(this, arguments)
    reapplySwatchDataOnFilterChange()
  }
  window.addEventListener('popstate', reapplySwatchDataOnFilterChange)
})
